import React, { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { 
  ArrowUpCircle, 
  ArrowDownCircle, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Filter,
  Download,
  Calendar,
  User,
  Users,
  RefreshCw,
  Search,
  X,
  Clock,
  AlertCircle,
  Gift
} from 'lucide-react';

const Transactions = () => {
  const toastTimeoutRef = useRef(null);
  const cacheRef = useRef({});
  const globalStatsRef = useRef(null); // Store initial global stats
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [specificDate, setSpecificDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('');
  
  // Global statistics - NEVER CHANGES after initial load
  const [globalStats, setGlobalStats] = useState({
    totalProfit: 0,
    totalDeductions: 0,
    totalRefunds: 0,
    totalDepositsToAgents: 0,
    totalDepositsToDrivers: 0,
    totalTransactionCount: 0
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [totalTransactions, setTotalTransactions] = useState(0);

  // Safe toast function - no alerts
  const showToast = (message, type = 'info') => {
    console.log(`[${type?.toUpperCase()}] ${message}`);
    
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    
    setToastMessage(message);
    setToastType(type);
    
    toastTimeoutRef.current = setTimeout(() => {
      setToastMessage('');
      setToastType('');
    }, 3000);
  };

  // Generate cache key
  const getCacheKey = (page, filters) => {
    return `${page}-${filters.type || 'all'}-${filters.startDate || 'all'}-${filters.endDate || 'all'}-${filters.search || ''}`;
  };

  // Fetch transactions using Cloud Function with caching
  const fetchTransactions = async (page = 1, filters = {}, isInitialLoad = false) => {
    try {
      const cacheKey = getCacheKey(page, filters);
      
      // Check cache first
      if (cacheRef.current[cacheKey]) {
        console.log('Loading from cache:', cacheKey);
        const cachedData = cacheRef.current[cacheKey];
        const { transactions: txns, pagination } = cachedData;
        
        setTransactions(txns);
        setFilteredTransactions(txns);
        setCurrentPage(pagination.currentPage);
        setTotalPages(pagination.totalPages);
        setTotalTransactions(pagination.totalCount);
        setHasNextPage(pagination.hasNextPage);
        setHasPrevPage(pagination.hasPrevPage);
        
        // Only set global stats on initial load
        if (isInitialLoad && globalStatsRef.current) {
          setGlobalStats(globalStatsRef.current);
        }
        
        return;
      }

      setLoading(true);
      
      const getAllTransactions = httpsCallable(functions, 'getAllTransactions');
      
      const requestData = {
        page: page,
        limit: itemsPerPage,
        startDate: filters.startDate || null,
        endDate: filters.endDate || null,
        type: filters.type || null,
        search: filters.search || null,
        // Skip server-side stats calculation when we already have them
        // cached. Cuts response time roughly in half on page nav, since
        // the function used to recompute stats on every call.
        includeStats: isInitialLoad,
      };

      console.log('Fetching transactions with:', requestData);

      const result = await getAllTransactions(requestData);
      
      if (result.data.success) {
        const { transactions: txns, stats, pagination } = result.data;
        
        // Parse timestamps for display
        const parsedTransactions = txns.map(txn => ({
          ...txn,
          timestamp: new Date(txn.createdAt),
          amount: parseFloat(txn.amount) || 0
        }));
        
        // Cache the result (WITHOUT stats)
        cacheRef.current[cacheKey] = {
          transactions: parsedTransactions,
          pagination: pagination
        };
        
        setTransactions(parsedTransactions);
        setFilteredTransactions(parsedTransactions);
        
        // ONLY store global stats on initial load
        if (isInitialLoad) {
          globalStatsRef.current = stats;
          setGlobalStats({
            totalProfit: stats.totalProfit || 0,
            totalDeductions: stats.totalDeductions || 0,
            totalRefunds: stats.totalRefunds || 0,
            totalDepositsToAgents: stats.totalDepositsToAgents || 0,
            totalDepositsToDrivers: stats.totalDepositsToDrivers || 0,
            totalTransactionCount: stats.totalTransactionCount || pagination.totalCount
          });
        }
        
        // Set pagination info
        setCurrentPage(pagination.currentPage);
        setTotalPages(pagination.totalPages);
        setTotalTransactions(pagination.totalCount);
        setHasNextPage(pagination.hasNextPage);
        setHasPrevPage(pagination.hasPrevPage);
      } else {
        showToast('Failed to fetch transactions', 'error');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      showToast(`Error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Initial load only on mount
  useEffect(() => {
    const filters = {
      type: null,
      startDate: null,
      endDate: null
    };
    
    // Initial load without filters - sets global stats
    fetchTransactions(1, filters, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle filter changes - reset to page 1 WITHOUT changing global stats
  useEffect(() => {
    const filters = {
      type: filterType !== 'all' ? filterType : null,
      startDate: getStartDateForFilter(),
      endDate: getEndDateForFilter(),
      search: debouncedSearch || null,
    };

    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      // Fetch filtered data but DON'T update global stats
      fetchTransactions(1, filters, false);
    }
  }, [filterType, dateRange, specificDate, startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle page changes
  useEffect(() => {
    if (currentPage > 0) {
      fetchTransactions(currentPage, {
        type: filterType !== 'all' ? filterType : null,
        startDate: getStartDateForFilter(),
        endDate: getEndDateForFilter(),
        search: debouncedSearch || null,
      }, false);
    }
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get start date based on filter
  const getStartDateForFilter = () => {
    const now = new Date();
    
    if (dateRange === 'today') {
      return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    } else if (dateRange === 'week') {
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    } else if (dateRange === 'month') {
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    } else if (dateRange === 'specific' && specificDate) {
      return new Date(specificDate).toISOString();
    } else if (dateRange === 'range' && startDate) {
      return new Date(startDate).toISOString();
    }
    
    return null;
  };

  // Get end date based on filter
  const getEndDateForFilter = () => {
    if (dateRange === 'specific' && specificDate) {
      const date = new Date(specificDate);
      date.setHours(23, 59, 59, 999);
      return date.toISOString();
    } else if (dateRange === 'range' && endDate) {
      const date = new Date(endDate);
      date.setHours(23, 59, 59, 999);
      return date.toISOString();
    }
    
    return null;
  };

  // Debounce the search box so we don't fire a Cloud Function call on
  // every keystroke. 300ms feels responsive without flooding the backend.
  // The server applies the search across ALL pages of the current
  // type/date filter, so a user whose transactions live deep in the
  // result set is now findable — previously the search was applied
  // only to the current page's data in memory.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Refetch from the server whenever the debounced search changes.
  useEffect(() => {
    const filters = {
      type: filterType !== 'all' ? filterType : null,
      startDate: getStartDateForFilter(),
      endDate: getEndDateForFilter(),
      search: debouncedSearch || null,
    };
    if (currentPage !== 1) {
      setCurrentPage(1); // page-change effect will refetch
    } else {
      fetchTransactions(1, filters, false);
    }
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pagination functions
  const goToNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  };
  
  const goToPreviousPage = () => {
    if (hasPrevPage) {
      setCurrentPage(prev => prev - 1);
    }
  };
  
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const refreshData = () => {
    // Clear cache on manual refresh
    cacheRef.current = {};
    
    fetchTransactions(currentPage, {
      type: filterType !== 'all' ? filterType : null,
      startDate: getStartDateForFilter(),
      endDate: getEndDateForFilter()
    }, false);
  };

// Fetch ALL transactions for export (respects filters but gets all pages)
const fetchAllTransactionsForExport = async (filters = {}) => {
  try {
    setLoading(true);
    
    const getAllTransactions = httpsCallable(functions, 'getAllTransactions');
    
    // Fetch with very high limit to get all transactions matching filters
    const requestData = {
      page: 1,
      limit: 10000, // Large limit to get all filtered transactions
      startDate: filters.startDate || null,
      endDate: filters.endDate || null,
      type: filters.type || null
    };

    console.log('Fetching all transactions for export with filters:', requestData);

    const result = await getAllTransactions(requestData);
    
    if (result.data.success) {
      const { transactions: txns } = result.data;
      
      // Parse timestamps for display
      const parsedTransactions = txns.map(txn => ({
        ...txn,
        timestamp: new Date(txn.createdAt),
        amount: parseFloat(txn.amount) || 0
      }));

      return parsedTransactions;
    } else {
      showToast('Failed to fetch transactions for export', 'error');
      return [];
    }
  } catch (error) {
    console.error('Error fetching transactions for export:', error);
    showToast(`Error: ${error.message}`, 'error');
    return [];
  } finally {
    setLoading(false);
  }
};

// Updated CSV Export - Gets ALL filtered/unfiltered transactions
const exportToCSV = async () => {
  let dataToExport = [];
  let fileName = '';
  let exportDescription = '';

  // Check if any filters are applied
  const hasTypeFilter = filterType !== 'all';
  const hasDateFilter = dateRange !== 'all';
  const hasSearchFilter = searchTerm.trim().length > 0;
  const isFiltered = hasTypeFilter || hasDateFilter || hasSearchFilter;

  // Build filters object
  const filters = {
    type: hasTypeFilter ? filterType : null,
    startDate: hasDateFilter ? getStartDateForFilter() : null,
    endDate: hasDateFilter ? getEndDateForFilter() : null
  };

  showToast(isFiltered ? 'Fetching all filtered transactions...' : 'Fetching all transactions...', 'info');

  // Fetch ALL transactions (with or without filters)
  dataToExport = await fetchAllTransactionsForExport(filters);

  // Apply search filter locally if search term exists
  if (hasSearchFilter && dataToExport.length > 0) {
    const search = searchTerm.toLowerCase();
    dataToExport = dataToExport.filter(txn => 
      txn.id?.toLowerCase().includes(search) ||
      txn.description?.toLowerCase().includes(search) ||
      txn.agentName?.toLowerCase().includes(search) ||
      txn.driverName?.toLowerCase().includes(search) ||
      txn.driverPhone?.toLowerCase().includes(search) ||
      txn.agentPhone?.toLowerCase().includes(search) ||
      txn.agentEmail?.toLowerCase().includes(search) ||
      txn.amount?.toString().includes(search)
    );
  }

  if (dataToExport.length === 0) {
    showToast('No transactions to export', 'error');
    return;
  }

  // Generate file name based on filters
  let fileNameSuffix = new Date().toISOString().split('T')[0];
  if (isFiltered) {
    if (hasTypeFilter && hasDateFilter) {
      fileName = `transactions_${filterType}_${fileNameSuffix}.csv`;
    } else if (hasTypeFilter) {
      fileName = `transactions_${filterType}_${fileNameSuffix}.csv`;
    } else if (hasDateFilter) {
      fileName = `transactions_${dateRange}_${fileNameSuffix}.csv`;
    } else if (hasSearchFilter) {
      fileName = `transactions_search_${fileNameSuffix}.csv`;
    }
  } else {
    fileName = `transactions_all_${fileNameSuffix}.csv`;
  }

  const headers = ['Date', 'Type', 'Amount (JOR)', 'Source', 'Description', 'User', 'Status'];
  const rows = dataToExport.map(txn => [
    txn.timestamp.toLocaleString('en-GB'),
    txn.type,
    txn.amount.toFixed(2),
    txn.source,
    txn.description || 'N/A',
    txn.source === 'agent' ? txn.agentName : txn.driverName,
    txn.status || 'N/A'
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);

  showToast(
    isFiltered 
      ? `Exported ${dataToExport.length} filtered transactions` 
      : `Exported ${dataToExport.length} transactions (all pages)`,
    'success'
  );
};

  const getTypeColor = (type) => {
    switch (type) {
      case 'deposit': return 'bg-blue-100 text-blue-800';
      case 'deposit-driver': return 'bg-green-100 text-green-800';
      case 'deduction': return 'bg-yellow-100 text-yellow-800';
      case 'refund': return 'bg-red-100 text-red-800';
      case 'topup': return 'bg-purple-100 text-purple-800';
      case 'withdraw': return 'bg-orange-100 text-orange-800';
      case 'gift': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'deposit':
      case 'deposit-driver':
      case 'topup':
        return <ArrowDownCircle className="w-5 h-5" />;
      case 'deduction':
      case 'withdraw':
        return <ArrowUpCircle className="w-5 h-5" />;
      case 'refund':
        return <RefreshCw className="w-5 h-5" />;
      case 'gift':
        return <Gift className="w-5 h-5" />;
      default:
        return <DollarSign className="w-5 h-5" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      // Driver-side `deposit` rows are agent → driver top-ups. The
      // agent-side `deposit-driver` mirror is dropped server-side, so
      // every `deposit` row we render here is the driver receiving from
      // an agent — label it that way to avoid the "the driver deposited
      // money" misreading.
      case 'deposit': return 'Top-up from Agent';
      case 'deposit-driver': return 'Deposit to Driver';
      case 'deduction': return 'Deduction';
      case 'refund': return 'Refund';
      case 'topup': return 'Agent Top-up';
      case 'withdraw': return 'Withdrawal';
      case 'gift': return 'Gift';
      default: return type;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-blue-50/20 p-6 space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white font-medium z-50 animate-in fade-in slide-in-from-top-2 ${
          toastType === 'success' ? 'bg-green-500' : 
          toastType === 'error' ? 'bg-red-500' : 
          'bg-blue-500'
        }`}>
          {toastMessage}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            Financial Transactions
          </h1>
          <p className="text-gray-600 mt-2 flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Complete transaction history and analytics
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={refreshData}
            className="px-5 py-2.5 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-purple-300 transition-all duration-200 flex items-center gap-2 shadow-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={exportToCSV}
            className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-200 flex items-center gap-2 shadow-lg font-medium"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Global Stats Cards - STATIC (NEVER CHANGES) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Global Profit - Shows Deductions Value */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-green-100 hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Global Profit</p>
              <p className="text-3xl font-bold text-green-600 mt-2">
                {globalStats.totalDeductions.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> JOR (All Time)
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-emerald-500 p-4 rounded-xl shadow-md">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Global Refunds */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-red-100 hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Global Refunds</p>
              <p className="text-3xl font-bold text-red-600 mt-2">
                {globalStats.totalRefunds.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <TrendingDown className="w-3 h-3" /> JOR (All Time)
              </p>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-pink-500 p-4 rounded-xl shadow-md">
              <RefreshCw className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Global Deposits to Agents */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-blue-100 hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Agent Deposits</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">
                {globalStats.totalDepositsToAgents.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <Users className="w-3 h-3" /> JOR (All Time)
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-indigo-500 p-4 rounded-xl shadow-md">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <label className="text-sm font-medium text-gray-700 mb-1 block">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
              />
            </div>
          </div>

          {/* Type Filter */}
          <div className="relative">
            <label className="text-sm font-medium text-gray-700 mb-1 block">Transaction Type</label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none z-10" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all appearance-none bg-white cursor-pointer"
              >
                <option value="all">All Types</option>
                <option value="deposit">Top-up from Agent</option>
                <option value="deposit-driver">Deposit to Driver</option>
                <option value="deduction">Deduction</option>
                <option value="refund">Refund</option>
              </select>
            </div>
          </div>

          {/* Date Range */}
          <div className="relative">
            <label className="text-sm font-medium text-gray-700 mb-1 block">Date Range</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none z-10" />
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all appearance-none bg-white cursor-pointer"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="specific">Specific Date</option>
                <option value="range">Date Range</option>
              </select>
            </div>
          </div>
        </div>

        {/* Specific Date Input */}
        {dateRange === 'specific' && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="max-w-md">
              <label className="text-sm font-medium text-gray-700 mb-1 block flex items-center gap-2">
                <Calendar className="w-4 h-4 text-purple-600" />
                Select Specific Date
              </label>
              <input
                type="date"
                value={specificDate}
                onChange={(e) => setSpecificDate(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
              />
            </div>
          </div>
        )}

        {/* Date Range Inputs */}
        {dateRange === 'range' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block flex items-center gap-2">
                <Calendar className="w-4 h-4 text-purple-600" />
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block flex items-center gap-2">
                <Calendar className="w-4 h-4 text-purple-600" />
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
              />
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-40 bg-white rounded-2xl shadow-lg border border-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-purple-600 mx-auto"></div>
            <p className="mt-3 text-gray-600 font-medium">Loading transactions...</p>
          </div>
        </div>
      )}

      {/* Transactions Table */}
      {!loading && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    From/To
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <AlertCircle className="w-12 h-12 text-gray-300 mb-3" />
                        <p className="text-gray-500 font-medium">No transactions found</p>
                        <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or refresh the page</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredTransactions.map((txn) => (
                    <tr 
                      key={`${txn.source}-${txn.id}`} 
                      className="hover:bg-purple-50/50 transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedTransaction(txn);
                        setShowDetailsModal(true);
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {txn.timestamp.toLocaleDateString('en-GB')}
                            </div>
                            <div className="text-xs text-gray-500">
                              {txn.timestamp.toLocaleTimeString('en-GB')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${getTypeColor(txn.type)}`}>
                          {getTypeIcon(txn.type)}
                          {getTypeLabel(txn.type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-bold text-gray-900">
                            {txn.amount.toFixed(2)}
                          </span>
                          <span className="text-xs text-gray-500">JOR</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-purple-100 to-blue-100 rounded-xl flex items-center justify-center">
                            {txn.source === 'agent' ? (
                              <Users className="w-5 h-5 text-purple-600" />
                            ) : (
                              <User className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {txn.source === 'agent' ? txn.agentName : txn.driverName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {txn.source === 'agent' 
                                ? `Agent • ${txn.agentEmail || 'N/A'}` 
                                : `Driver • ${txn.driverPhone || 'N/A'}`}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 max-w-xs">
                        <div className="text-sm text-gray-900 truncate" title={txn.description}>
                          {txn.description || 'No description'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTransaction(txn);
                            setShowDetailsModal(true);
                          }}
                          className="text-purple-600 hover:text-purple-800 font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 0 && (
            <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700 font-medium">
                  Page {currentPage} of {totalPages}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={goToPreviousPage}
                  disabled={!hasPrevPage}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    !hasPrevPage
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-purple-600 hover:bg-purple-50 border-2 border-purple-200 hover:border-purple-400'
                  }`}
                >
                  Previous
                </button>
                
                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {totalPages <= 7 ? (
                    Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => goToPage(page)}
                        className={`w-10 h-10 rounded-lg font-medium transition-all ${
                          currentPage === page
                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                            : 'bg-white text-gray-700 hover:bg-purple-50 border-2 border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        {page}
                      </button>
                    ))
                  ) : (
                    <>
                      {currentPage > 3 && (
                        <>
                          <button
                            onClick={() => goToPage(1)}
                            className="w-10 h-10 rounded-lg font-medium bg-white text-gray-700 hover:bg-purple-50 border-2 border-gray-200 hover:border-purple-300 transition-all"
                          >
                            1
                          </button>
                          {currentPage > 4 && <span className="px-2 text-gray-500">...</span>}
                        </>
                      )}
                      
                      {Array.from({ length: 5 }, (_, i) => {
                        const pageNumber = currentPage - 2 + i;
                        if (pageNumber < 1 || pageNumber > totalPages) return null;
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => goToPage(pageNumber)}
                            className={`w-10 h-10 rounded-lg font-medium transition-all ${
                              currentPage === pageNumber
                                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                                : 'bg-white text-gray-700 hover:bg-purple-50 border-2 border-gray-200 hover:border-purple-300'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                      
                      {currentPage < totalPages - 2 && (
                        <>
                          {currentPage < totalPages - 3 && <span className="px-2 text-gray-500">...</span>}
                          <button
                            onClick={() => goToPage(totalPages)}
                            className="w-10 h-10 rounded-lg font-medium bg-white text-gray-700 hover:bg-purple-50 border-2 border-gray-200 hover:border-purple-300 transition-all"
                          >
                            {totalPages}
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
                
                <button
                  onClick={goToNextPage}
                  disabled={!hasNextPage}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    !hasNextPage
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-purple-600 hover:bg-purple-50 border-2 border-purple-200 hover:border-purple-400'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-4 flex justify-between items-center rounded-t-2xl">
              <div>
                <h2 className="text-2xl font-bold">Transaction Details</h2>
                <p className="text-purple-100 text-sm mt-1">
                  {selectedTransaction.timestamp.toLocaleString('en-GB')}
                </p>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Transaction Type & Amount */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border-2 border-purple-100">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Transaction Type</p>
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${getTypeColor(selectedTransaction.type)}`}>
                    {getTypeIcon(selectedTransaction.type)}
                    {getTypeLabel(selectedTransaction.type)}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600 mb-1">Amount</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {selectedTransaction.amount.toFixed(2)} JOR
                  </p>
                </div>
              </div>

              {/* Source Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedTransaction.source === 'agent' && selectedTransaction.agentName && (
                  <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                    <p className="text-sm font-semibold text-purple-900 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Agent Information
                    </p>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-600">Name:</span>
                        <span className="ml-2 font-medium text-gray-900">{selectedTransaction.agentName}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Email:</span>
                        <span className="ml-2 font-medium text-gray-900">{selectedTransaction.agentEmail || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">ID:</span>
                        <span className="ml-2 font-mono text-xs text-gray-700">{selectedTransaction.agentId}</span>
                      </div>
                    </div>
                  </div>
                )}

                {selectedTransaction.source === 'driver' && selectedTransaction.driverName && (
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Driver Information
                    </p>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-600">Name:</span>
                        <span className="ml-2 font-medium text-gray-900">{selectedTransaction.driverName}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Phone:</span>
                        <span className="ml-2 font-medium text-gray-900">{selectedTransaction.driverPhone || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">ID:</span>
                        <span className="ml-2 font-mono text-xs text-gray-700">{selectedTransaction.driverId}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Transaction Details */}
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-gray-900 mb-3">Transaction Details</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Description:</span>
                    <span className="font-medium text-gray-900 text-right max-w-xs truncate">{selectedTransaction.description || 'No description'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transaction ID:</span>
                    <span className="font-mono text-xs text-gray-900">{selectedTransaction.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date & Time:</span>
                    <span className="font-medium text-gray-900">{selectedTransaction.timestamp.toLocaleString('en-GB')}</span>
                  </div>
                </div>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowDetailsModal(false)}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
